'use client';

/**
 * useAffiliatePromoSlots
 *
 * Shared hook that builds bannerSlot (TopBanner) and promoFooterSlot
 * ("Maybe later" button + offer expiry) for the affiliatePromo flow mode.
 *
 * Used by:
 * - MobilePurchaseFlow (onMaybeLater sends mobile messages)
 * - PurchaseFlowWrapper (onMaybeLater calls wrapper callbacks)
 *
 * Returns undefined for both slots when isAffiliatePromo is false.
 */

import React, { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import TopBanner from '../../../../common/top_banner';
import {
  isPrerenderMode, getXfmFreeSetupMin, getXfmDefaultMinutes, isXFMActive
} from '../../../config/util';
import { PURCHASE_FLOW_TEXT_KEYS } from '../utils/constants';
import { resolveTextKey } from '../../../../common/config/purchaseFlowRenderHelpers';
import classes from '../screens/classes.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface AffiliatePromoSlotsParams {
  isAffiliatePromo: boolean;
  userBonusInfos?: any[];
  anonymousUserBonusInfos?: any[];
  userId?: number;
  userXfmProgram?: any;
  /** Explicit expiry override (e.g., from purchaseDetails). Falls back to the active offer's expiry. */
  expiresAt?: string;
  onMaybeLater: () => void;
  /** Translation function from config (renderText). */
  renderText: (key: string, params?: Record<string, string | number>) => string;
  /** Current locale code (e.g. 'en', 'es') for date formatting. */
  locale: string;
}

export interface AffiliatePromoSlots {
  bannerSlot: React.ReactNode | undefined;
  promoFooterSlot: React.ReactNode | undefined;
}

// ============================================================================
// Hook
// ============================================================================

export function useAffiliatePromoSlots({
  isAffiliatePromo,
  userBonusInfos,
  anonymousUserBonusInfos,
  userId,
  userXfmProgram,
  expiresAt,
  onMaybeLater,
  renderText,
  locale
}: AffiliatePromoSlotsParams): AffiliatePromoSlots {
  const bannerSlot = useMemo(() => {
    if (!isAffiliatePromo) return undefined;

    const topBannerProps = {
      userBonusInfos,
      anonymousUserBonusInfos,
      userXfmProgram,
      userId,
      shouldShowMarketingBanner: true,
      xfmFreeSetupMin: getXfmFreeSetupMin(),
      xfmDefaultMinutes: getXfmDefaultMinutes(),
      isXFMActive: isXFMActive(),
      isPrerenderMode: isPrerenderMode()
    };

    return <TopBanner topBannerProps={ topBannerProps } />;
  }, [isAffiliatePromo, userBonusInfos, anonymousUserBonusInfos, userXfmProgram, userId]);

  const promoFooterSlot = useMemo(() => {
    if (!isAffiliatePromo) return undefined;

    const activeOffer = userBonusInfos?.[0] || anonymousUserBonusInfos?.[0];
    const expireDate = expiresAt || activeOffer?.expiresAt || activeOffer?.expires_at;

    const renderOfferExpiry = () => {
      if (!expireDate) return null;
      const dateDiff = parseInt(
        String((new Date(expireDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
        10
      );
      if (dateDiff > 7) return null;

      const localeMap = { en: enUS, es } as Record<string, typeof enUS>;
      const dateString = format(
        new Date(expireDate),
        'MMMM dd, yyyy',
        { locale: localeMap[locale] }
      );

      return (
        <div className={ classes.offerExpiresDateContainer }>
          <Trans
            i18nKey={ resolveTextKey(PURCHASE_FLOW_TEXT_KEYS.PROMO_OFFER_EXPIRES) }
            components={ {
              bold: <span className={ classes.offerExpiresDateBoldText } />,
              brIfNeed: <br className={ classes.offerExpiresBrIfNeed } />
            } }
            values={ { dateString } }
          />
        </div>
      );
    };

    return (
      <>
        <button
          type="button"
          className={ classes.noThanksLink }
          onClick={ onMaybeLater }
        >
          { renderText(PURCHASE_FLOW_TEXT_KEYS.PROMO_MAYBE_LATER) }
        </button>
        { renderOfferExpiry() }
      </>
    );
  }, [isAffiliatePromo, userBonusInfos, anonymousUserBonusInfos, expiresAt, onMaybeLater, renderText, locale]);

  return { bannerSlot, promoFooterSlot };
}
